/**
 * Rax Audit Trail Component
 *
 * Full audit log viewer with filtering and export.
 */

import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";
import { raxApi } from "../../api/rax-api.js";
import { raxTheme } from "../styles/rax-theme.js";

interface AuditEntry {
  id: string;
  action: string;
  details: Record<string, any>;
  costCents: number | null;
  model: string | null;
  tokensIn: number | null;
  tokensOut: number | null;
  durationMs: number | null;
  timestamp: string;
}

type AuditExportFormat = "json" | "csv" | "jsonl";

@customElement("rax-audit-trail")
export class RaxAuditTrail extends LitElement {
  static override styles = [
    raxTheme,
    css`
      :host {
        display: block;
      }
      
      .audit-container {
        padding: var(--space-6);
      }
      
      .audit-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: var(--space-5);
      }
      
      .audit-title {
        font-size: var(--font-lg);
        font-weight: 700;
        color: var(--text-primary);
      }
      
      .header-actions {
        display: flex;
        gap: var(--space-2);
      }
      
      .action-btn {
        padding: var(--space-2) var(--space-3);
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        color: var(--text-secondary);
        font-size: var(--font-xs);
        cursor: pointer;
        transition: all var(--transition-fast);
      }
      
      .action-btn:hover {
        border-color: var(--rax-yellow);
        color: var(--rax-yellow);
      }
      
      /* Filter bar */
      .filter-bar {
        display: flex;
        gap: var(--space-2);
        margin-bottom: var(--space-4);
        flex-wrap: wrap;
      }
      
      .filter-chip {
        padding: var(--space-1) var(--space-3);
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-full);
        color: var(--text-secondary);
        font-size: var(--font-xs);
        cursor: pointer;
        transition: all var(--transition-fast);
      }
      
      .filter-chip:hover {
        border-color: var(--text-tertiary);
      }
      
      .filter-chip.active {
        background: var(--rax-yellow);
        color: var(--rax-black);
        border-color: var(--rax-yellow);
        font-weight: 600;
      }
      
      /* Entries list */
      .entries-list {
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
      }
      
      .entry {
        display: grid;
        grid-template-columns: 140px 1fr 80px 80px;
        gap: var(--space-3);
        padding: var(--space-3) var(--space-4);
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        align-items: center;
        font-size: var(--font-sm);
        transition: border-color var(--transition-fast);
        cursor: pointer;
      }
      
      .entry:hover {
        border-color: var(--text-tertiary);
      }
      
      .entry.expanded {
        border-color: var(--rax-yellow);
      }
      
      .entry-time {
        font-family: ui-monospace, monospace;
        font-size: var(--font-xs);
        color: var(--text-tertiary);
      }
      
      .entry-action {
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }
      
      .entry-action-icon {
        font-size: 16px;
      }
      
      .entry-action-text {
        color: var(--text-primary);
        font-weight: 500;
      }
      
      .entry-model {
        font-size: var(--font-xs);
        color: var(--text-secondary);
        font-family: ui-monospace, monospace;
      }
      
      .entry-cost {
        font-size: var(--font-sm);
        font-weight: 600;
        color: var(--rax-yellow);
        text-align: right;
      }
      
      .entry-cost.zero {
        color: var(--text-tertiary);
      }
      
      .entry-details {
        grid-column: 1 / -1;
        margin-top: var(--space-1);
        border-top: 1px solid var(--border-color);
        padding-top: var(--space-3);
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
      }
      
      .entry-meta {
        display: flex;
        gap: var(--space-4);
        flex-wrap: wrap;
        color: var(--text-secondary);
        font-size: var(--font-xs);
      }
      
      .entry-details-json {
        margin: 0;
        padding: var(--space-3);
        border-radius: var(--radius-md);
        border: 1px solid var(--border-color);
        background: var(--bg-primary);
        color: var(--text-secondary);
        font-size: 11px;
        line-height: 1.45;
        max-height: 260px;
        overflow: auto;
        white-space: pre-wrap;
        word-break: break-word;
      }
      
      .empty-state {
        text-align: center;
        padding: var(--space-8);
        color: var(--text-tertiary);
      }
      
      .loading {
        text-align: center;
        padding: var(--space-8);
        color: var(--text-tertiary);
      }
      
      .load-more {
        text-align: center;
        padding: var(--space-4);
      }
      
      .load-more-btn {
        padding: var(--space-2) var(--space-4);
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        color: var(--text-secondary);
        font-size: var(--font-sm);
        cursor: pointer;
      }
    `,
  ];

  @state()
  private entries: AuditEntry[] = [];

  @state()
  private loading = true;

  @state()
  private filter: string | null = null;

  @state()
  private expandedEntryId: string | null = null;

  override async connectedCallback() {
    super.connectedCallback();
    await this.loadEntries();
  }

  private async loadEntries() {
    this.loading = true;
    try {
      const res = await raxApi.request<AuditEntry[]>("GET", "/api/rax/audit?limit=100");
      if (res.success && res.data) {
        this.entries = res.data;
      }
    } catch (err) {
      console.error("[AuditTrail] Failed to load:", err);
    } finally {
      this.loading = false;
    }
  }

  private get filteredEntries(): AuditEntry[] {
    if (!this.filter) return this.entries;
    return this.entries.filter((e) => e.action.includes(this.filter!));
  }

  private getActionIcon(action: string): string {
    if (action.includes("llm")) return "🤖";
    if (action.includes("tool")) return "🔧";
    if (action.includes("skill")) return "📦";
    if (action.includes("auth")) return "🔐";
    if (action.includes("error")) return "⚠️";
    return "📋";
  }

  private formatTime(ts: string): string {
    const d = new Date(ts);
    return d.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  }

  private formatCost(cents: number | null): string {
    if (!cents || cents === 0) return "—";
    return `$${(cents / 100).toFixed(4)}`;
  }

  private toggleEntryDetails(id: string) {
    this.expandedEntryId = this.expandedEntryId === id ? null : id;
  }

  private formatDetails(details: Record<string, any> | null | undefined): string {
    if (!details || typeof details !== "object" || Object.keys(details).length === 0) {
      return "No details available for this event.";
    }
    try {
      return JSON.stringify(details, null, 2);
    } catch {
      return "Details could not be rendered.";
    }
  }

  private async exportAudit(format: AuditExportFormat) {
    try {
      const clerk = (window as any).Clerk || (window as any).clerkClient;
      const token = clerk?.session ? await clerk.session.getToken() : raxApi.getToken();

      const response = await fetch(`/api/rax/audit/export?format=${encodeURIComponent(format)}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });

      if (!response.ok) {
        const errText = await response.text().catch(() => "");
        throw new Error(errText || `HTTP ${response.status}`);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rax-audit-${new Date().toISOString().slice(0, 10)}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(`[AuditTrail] Export failed (${format}):`, err);
    }
  }

  private async startMonitoring() {
    try {
      const res = await raxApi.request("POST", "/api/rax/audit/start");
      if (res.success) {
        console.log("[AuditTrail] Started audit monitoring");
        // Refresh entries to show new audit data after a short delay
        setTimeout(() => this.loadEntries(), 2000);
      }
    } catch (err) {
      console.error("[AuditTrail] Start monitoring failed:", err);
    }
  }

  override render() {
    if (this.loading) {
      return html`
        <div class="loading">Loading audit trail...</div>
      `;
    }

    const actionTypes = [...new Set(this.entries.map((e) => e.action))];

    return html`
      <div class="audit-container">
        <div class="audit-header">
          <div class="audit-title">📋 Audit Trail</div>
          <div class="header-actions">
            <button class="action-btn" @click=${() => this.startMonitoring()}>⚡ Start Monitoring</button>
            <button class="action-btn" @click=${() => this.loadEntries()}>🔄 Refresh</button>
            <button class="action-btn" @click=${() => this.exportAudit("json")}>📥 JSON</button>
            <button class="action-btn" @click=${() => this.exportAudit("csv")}>📥 CSV</button>
            <button class="action-btn" @click=${() => this.exportAudit("jsonl")}>📥 JSONL</button>
          </div>
        </div>

        <div class="filter-bar">
          <span
            class="filter-chip ${!this.filter ? "active" : ""}"
            @click=${() => (this.filter = null)}
          >All</span>
          ${actionTypes.slice(0, 8).map(
            (type) => html`
            <span
              class="filter-chip ${this.filter === type ? "active" : ""}"
              @click=${() => (this.filter = this.filter === type ? null : type)}
            >${type}</span>
          `,
          )}
        </div>

        ${
          this.filteredEntries.length > 0
            ? html`
          <div class="entries-list">
            ${this.filteredEntries.map(
              (entry) => html`
              <div
                class="entry ${this.expandedEntryId === entry.id ? "expanded" : ""}"
                @click=${() => this.toggleEntryDetails(entry.id)}
              >
                <span class="entry-time">${this.formatTime(entry.timestamp)}</span>
                <div class="entry-action">
                  <span class="entry-action-icon">${this.getActionIcon(entry.action)}</span>
                  <span class="entry-action-text">${entry.action}</span>
                </div>
                <span class="entry-model">${entry.model ?? ""}</span>
                <span class="entry-cost ${!entry.costCents ? "zero" : ""}">
                  ${this.formatCost(entry.costCents)}
                </span>
                ${
                  this.expandedEntryId === entry.id
                    ? html`
                  <div class="entry-details">
                    <div class="entry-meta">
                      ${
                        entry.tokensIn !== null || entry.tokensOut !== null
                          ? html`<span>Tokens: ${entry.tokensIn ?? 0} in / ${entry.tokensOut ?? 0} out</span>`
                          : ""
                      }
                      ${entry.durationMs !== null ? html`<span>Duration: ${entry.durationMs} ms</span>` : ""}
                    </div>
                    <pre class="entry-details-json">${this.formatDetails(entry.details)}</pre>
                  </div>
                `
                    : ""
                }
              </div>
            `,
            )}
          </div>
        `
            : html`
                <div class="empty-state">
                  <p>No audit entries yet. Activity will appear here as you use Rax.</p>
                </div>
              `
        }
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rax-audit-trail": RaxAuditTrail;
  }
}
